import type { FnolInboxRecord } from '~/server_shared/openapi/data-contracts';
import { FnolInboxRecordFnolInboxStatus } from '~/server_shared/openapi/data-contracts';

import { FnolRecommendationState } from '../types';

/**
 * Determines the appropriate FNOL recommendation state based on the inbox item properties
 *
 * @param selectedInboxItem - The FNOL inbox item to analyze
 * @returns The appropriate FnolRecommendationState enum value
 *
 * @description
 * This function implements the corrected state determination logic that properly
 * handles all processing states, policy selection states, and recommendation states.
 *
 * State determination logic:
 * 1. PROCESSING - AI is currently processing (status === 'processing')
 * 2. PROCESSING_FAILED - AI processing failed (status === 'processing_failed')
 * 3. UNKNOWN_SUBORG_LOB_SELECT_POLICY - No policy selected, needs selection (!policy && !was_sent_to_ai_service)
 * 4. POLICY_NOT_FOUND_BY_AI - Policy search attempted but none found (!policy && was_sent_to_ai_service && status !== 'processing')
 * 5. RECOMMENDATION_EXISTS - Has recommendations or successful processing (analysis_extra?.related_claims || status === 'new')
 */
export function determineFnolRecommendationState(selectedInboxItem: FnolInboxRecord): FnolRecommendationState {
  const { status, policy, was_sent_to_ai_service, analysis_extra } = selectedInboxItem;

  // Mid processing - AI is currently working
  if (status === FnolInboxRecordFnolInboxStatus.AiProcessing) {
    return FnolRecommendationState.PROCESSING;
  }

  // Processing failed - AI completed with failure
  if (status === FnolInboxRecordFnolInboxStatus.AiProcessingFailed) {
    return FnolRecommendationState.PROCESSING_FAILED;
  }

  // AI completed successfully — show as recommendation (even without policy for CASE 1/2)
  if (status === FnolInboxRecordFnolInboxStatus.AiNew) {
    return FnolRecommendationState.RECOMMENDATION_EXISTS;
  }

  // Policy not found - AI stopped processing early because policy was not found (error code 4)
  // OR policy search attempted but none found
  if (status === FnolInboxRecordFnolInboxStatus.AiPolicyNotFound || (!policy && was_sent_to_ai_service)) {
    return FnolRecommendationState.POLICY_NOT_FOUND_BY_AI;
  }

  // No policy selected - prompt user to select one
  if ((!policy && !was_sent_to_ai_service) || status === FnolInboxRecordFnolInboxStatus.NoLobAndSubOrg) {
    return FnolRecommendationState.UNKNOWN_SUBORG_LOB_SELECT_POLICY;
  }

  // Has recommendations (AiNew is already handled above)
  if (policy && analysis_extra?.related_claims) {
    return FnolRecommendationState.RECOMMENDATION_EXISTS;
  }

  // Default fallback
  return FnolRecommendationState.UNKNOWN_SUBORG_LOB_SELECT_POLICY;
}
